import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listFrequencies, updateFrequency, deleteFrequency } from "../services/frequency";
import { getUserInfo, hasSpecialHourSchedule } from "../services/auth";
import { formatCurrencyMZN } from "../utils/paymentHelpers";
import { getClassesLabels } from "../utils/classesLabels";
import { useConfirm } from "../hook/useConfirm";

const DURATION_UNIT_OPTIONS = ["Meses", "Anos"];

// Split out of the former CursosLayer, same "hide cadastro from the menu"
// philosophy applied to ListaDespesasLayer/ListaProdutosLayer/etc.: cadastro
// is only reachable via the "Adicionar" button below (see
// CadastrarClasseLayer / App.js route `/classes/lista/cadastrar`). Edit and
// delete stay here, same as the former CursosLayer's inline modal.
//
// The entity is called "Curso"/"Classe"/"Nível" depending on the logged-in
// user's sucursal (see src/utils/classesLabels.js) — the header, the
// "Adicionar" button and the edit modal title all follow that same term so
// the wording stays consistent with the sidebar.
const ListaClassesLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const labels = getClassesLabels();
  const term = t(labels.singular);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchCourses = () => {
    setLoading(true);
    listFrequencies()
      .then((resp) => setCourses(resp.data || []))
      .catch(() => toast.error(t("classe.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(fetchCourses, [t]);

  const openEdit = (course) => {
    setEditTarget(course);
    setEditForm({
      description: course.description || "",
      code: course.code || "",
      duration: course.duration || "",
      durationUnit: course.durationUnit || "Meses",
      registrationValue: course.registrationValue || "",
      recurigRegistrationValue: course.recurigRegistrationValue || "",
      monthlyPayment: course.monthlyPayment || "",
      specialHourMonthlyValue: course.specialHourMonthlyValue || "",
    });
  };
  const closeEdit = () => {
    setEditTarget(null);
    setEditForm(null);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await updateFrequency(editTarget.id, {
        description: editForm.description,
        code: editForm.code,
        duration: Number(editForm.duration),
        durationUnit: editForm.durationUnit,
        registrationValue: Number(editForm.registrationValue) || 0,
        recurigRegistrationValue: Number(editForm.recurigRegistrationValue) || 0,
        monthlyPayment: Number(editForm.monthlyPayment) || 0,
        specialHourMonthlyValue: Number(editForm.specialHourMonthlyValue) || 0,
        updatedBy: getUserInfo()?.id,
      });
      toast.success(t("classe.success.updated"));
      closeEdit();
      fetchCourses();
    } catch (err) {
      toast.error(t("classe.error.update"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (course) => {
    if (!(await confirm(t("classe.confirm.delete", { name: course.description })))) return;
    setDeletingId(course.id);
    try {
      await deleteFrequency(course.id);
      toast.success(t("classe.success.deleted"));
      fetchCourses();
    } catch (err) {
      toast.error(t("classe.error.delete"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t(labels.submenu)}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("classe.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/classes/lista/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("classe.list.add", { term })}
              </button>
            </div>

            {loading ? (
              <div className='text-center py-40'>{t("global.loading")}</div>
            ) : courses.length === 0 ? (
              <div className='alert alert-info mb-0'>{t("classe.hint.noItems")}</div>
            ) : (
              <div className='table-responsive'>
                <table className='table bordered-table mb-0'>
                  <thead>
                    <tr>
                      <th>{labels.isCursos ? t("classe.field.codeDescription") : t("classe.field.description")}</th>
                      <th>{t("classe.field.duration")}</th>
                      <th>{t("classe.field.registrationValue")}</th>
                      <th>{t("classe.field.monthlyPayment")}</th>
                      {hasSpecialHourSchedule() && <th>{t("classe.field.specialHourMonthlyValue")}</th>}
                      <th>{t("global.col.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((c) => (
                      <tr key={c.id}>
                        <td>{labels.isCursos && c.code ? `${c.code} - ${c.description}` : c.description}</td>
                        <td>
                          {c.duration ? `${c.duration} ${t("classe.field.duration.months")}` : "-"}
                        </td>
                        <td>{formatCurrencyMZN(c.registrationValue)}</td>
                        <td>{formatCurrencyMZN(c.monthlyPayment)}</td>
                        {hasSpecialHourSchedule() && (
                          <td>{c.specialHourMonthlyValue ? formatCurrencyMZN(c.specialHourMonthlyValue) : "-"}</td>
                        )}
                        <td>
                          <div className='d-flex gap-2'>
                            <button type='button' className='btn btn-outline-primary-600 btn-sm d-flex align-items-center gap-1' onClick={() => openEdit(c)}>
                              <Icon icon='mdi:pencil-outline' />
                              {t("global.edit")}
                            </button>
                            <button
                              type='button'
                              className='btn btn-outline-danger-600 btn-sm d-flex align-items-center gap-1'
                              disabled={deletingId === c.id}
                              onClick={() => handleDelete(c)}
                            >
                              <Icon icon='mdi:trash-can-outline' />
                              {t("global.delete")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editTarget && editForm && (
        <div className='modal show d-block' style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h6 className='mb-0'>{t("classe.action.edit", { term })}</h6>
                <button type='button' className='btn-close' onClick={closeEdit} />
              </div>
              <div className='modal-body'>
                <div className='row gy-3'>
                  <div className='col-sm-8'>
                    <label className='form-label'>{t("classe.field.description")}</label>
                    <div className='icon-field'>
                      <span className='icon'>
                        <Icon icon='solar:pen-2-outline' />
                      </span>
                      <input type='text' className='form-control' value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} />
                    </div>
                  </div>
                  <div className='col-sm-4'>
                    <label className='form-label'>{t("turma.field.code")}</label>
                    <div className='icon-field'>
                      <span className='icon'>
                        <Icon icon='solar:hashtag-square-outline' />
                      </span>
                      <input type='text' className='form-control' value={editForm.code} onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))} />
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("classe.field.duration")}</label>
                    <div className='d-flex gap-2'>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:pen-2-outline' />
                        </span>
                        <input type='number' min='1' className='form-control' value={editForm.duration} onChange={(e) => setEditForm((f) => ({ ...f, duration: e.target.value }))} />
                      </div>
                      <select className='form-select' value={editForm.durationUnit} onChange={(e) => setEditForm((f) => ({ ...f, durationUnit: e.target.value }))}>
                        {DURATION_UNIT_OPTIONS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("classe.field.monthlyPayment")}</label>
                    <div className='icon-field'>
                      <span className='icon'>
                        <Icon icon='solar:pen-2-outline' />
                      </span>
                      <input type='number' min='0' className='form-control' value={editForm.monthlyPayment} onChange={(e) => setEditForm((f) => ({ ...f, monthlyPayment: e.target.value }))} />
                    </div>
                  </div>
                  {hasSpecialHourSchedule() && (
                    <div className='col-sm-6'>
                      <label className='form-label'>{t("classe.field.specialHourMonthlyValue")}</label>
                      <div className='icon-field'>
                        <span className='icon'>
                          <Icon icon='solar:pen-2-outline' />
                        </span>
                        <input type='number' min='0' className='form-control' value={editForm.specialHourMonthlyValue} onChange={(e) => setEditForm((f) => ({ ...f, specialHourMonthlyValue: e.target.value }))} />
                      </div>
                    </div>
                  )}
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("classe.field.registrationValue")}</label>
                    <div className='icon-field'>
                      <span className='icon'>
                        <Icon icon='solar:pen-2-outline' />
                      </span>
                      <input
                        type='number'
                        min='0'
                        className='form-control'
                        value={editForm.registrationValue}
                        onChange={(e) => setEditForm((f) => ({ ...f, registrationValue: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <label className='form-label'>{t("classe.field.renewalValue")}</label>
                    <div className='icon-field'>
                      <span className='icon'>
                        <Icon icon='solar:pen-2-outline' />
                      </span>
                      <input
                        type='number'
                        min='0'
                        className='form-control'
                        value={editForm.recurigRegistrationValue}
                        onChange={(e) => setEditForm((f) => ({ ...f, recurigRegistrationValue: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-outline-secondary' onClick={closeEdit}>
                  {t("global.cancel")}
                </button>
                <button type='button' className='btn btn-primary-600' onClick={handleSaveEdit} disabled={saving}>
                  {saving ? t("global.loading") : <><i className='ri-save-line' /> {t("global.save")}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {ConfirmModal}
    </div>
  );
};

export default ListaClassesLayer;
